import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, FileText, StickyNote } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FileUploadField from '../ui/FileUploadField';
import { formatDateForAPI } from '../../utils/dateUtils';
import { useUpdateInvoice } from '../../hooks/useInvoices';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const EditInvoiceModal = ({ isOpen, onClose, invoice }) => {
    const [formData, setFormData] = useState({
        name: '',
        amount: '',
        issueDate: '',
        dueDate: '',
        paidDate: '',
        isPaid: false,
        notes: '',
        fileUid: null,
        paymentProofFileUid: null,
    });

    const [isUploading, setIsUploading] = useState(false);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const updateInvoice = useUpdateInvoice();
    const { t } = useLanguage();

    useEffect(() => {
        if (invoice) {
            setFormData({
                name: invoice.name || '',
                amount: invoice.amount?.toString() || '',
                issueDate: invoice.issue_date ? invoice.issue_date.split('T')[0] : '',
                dueDate: invoice.due_date ? invoice.due_date.split('T')[0] : '',
                paidDate: invoice.paid_date ? invoice.paid_date.split('T')[0] : '',
                isPaid: invoice.is_paid || false,
                notes: invoice.notes || '',
                fileUid: invoice.fileUid || null,
                paymentProofFileUid: invoice.paymentProofFileUid || null,
            });
        }
    }, [invoice]);

    const validateForm = () => {
        const newErrors = {};

        if (formData.name && formData.name.length > 191) {
            newErrors.name = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (formData.amount && parseFloat(formData.amount) < 0) {
            newErrors.amount = t(KEYS.INVOICE_AMOUNT_ERROR);
        }

        if (formData.dueDate && formData.issueDate && formData.dueDate < formData.issueDate) {
            newErrors.dueDate = t(KEYS.INVOICE_DUE_DATE_ERROR);
        }

        if (formData.paidDate && formData.issueDate && formData.paidDate < formData.issueDate) {
            newErrors.paidDate = t(KEYS.INVOICE_PAID_DATE_ERROR);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const data = {
                name: formData.name || undefined,
                amount: formData.amount ? parseFloat(formData.amount) : undefined,
                issue_date: formData.issueDate ? formatDateForAPI(formData.issueDate) : undefined,
                due_date: formData.dueDate ? formatDateForAPI(formData.dueDate) : undefined,
                paid_date: formData.paidDate ? formatDateForAPI(formData.paidDate) : undefined,
                is_paid: formData.isPaid,
                notes: formData.notes || undefined,
                file_uid: formData.fileUid ?? undefined,
                payment_proof_file_uid: formData.paymentProofFileUid ?? undefined,
            };

            await updateInvoice.mutateAsync({
                supplierUid: invoice.supplierUid,
                serviceUid: invoice.serviceUid,
                invoiceUid: invoice.uid,
                data,
            });

            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.INVOICE_EDIT_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.INVOICE_EDIT_TITLE)}
            subtitle="Update invoice details"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting || isUploading}>
                        {isSubmitting ? t(KEYS.INVOICE_EDIT_SUBMITTING) : t(KEYS.INVOICE_EDIT_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Service (read-only) */}
                {invoice?.serviceName && (
                    <div className="space-y-1">
                        <label className="label-base flex items-center gap-1.5">
                            <FileText className="w-4 h-4" />
                            Service
                        </label>
                        <div className="input-base bg-surface-overlay text-text-muted cursor-not-allowed">
                            {invoice.serviceName}
                        </div>
                    </div>
                )}

                {/* Name */}
                <div className="space-y-1">
                    <label htmlFor="name" className="label-base flex items-center gap-1.5">
                        <FileText className="w-4 h-4" />
                        Name
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`input-base ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., March 2026"
                        maxLength={191}
                        disabled={isSubmitting}
                    />
                    {errors.name && (
                        <span className="text-xs text-red-500 mt-1">{errors.name}</span>
                    )}
                </div>

                {/* Amount */}
                <div className="space-y-1">
                    <label htmlFor="amount" className="label-base flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4" />
                        Amount
                    </label>
                    <input
                        type="number"
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        className={`input-base ${errors.amount ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        disabled={isSubmitting}
                    />
                    {errors.amount && (
                        <span className="text-xs text-red-500 mt-1">{errors.amount}</span>
                    )}
                </div>

                {/* Issue Date */}
                <div className="space-y-1">
                    <label htmlFor="issueDate" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Issue Date
                    </label>
                    <input
                        type="date"
                        id="issueDate"
                        name="issueDate"
                        value={formData.issueDate}
                        onChange={handleChange}
                        className={`input-base ${errors.issueDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.issueDate && (
                        <span className="text-xs text-red-500 mt-1">{errors.issueDate}</span>
                    )}
                </div>

                {/* Due Date */}
                <div className="space-y-1">
                    <label htmlFor="dueDate" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Due Date
                    </label>
                    <input
                        type="date"
                        id="dueDate"
                        name="dueDate"
                        value={formData.dueDate}
                        onChange={handleChange}
                        className={`input-base ${errors.dueDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.dueDate && (
                        <span className="text-xs text-red-500 mt-1">{errors.dueDate}</span>
                    )}
                </div>

                {/* Paid Date */}
                <div className="space-y-1">
                    <label htmlFor="paidDate" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Paid Date
                    </label>
                    <input
                        type="date"
                        id="paidDate"
                        name="paidDate"
                        value={formData.paidDate}
                        onChange={handleChange}
                        className={`input-base ${errors.paidDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.paidDate && (
                        <span className="text-xs text-red-500 mt-1">{errors.paidDate}</span>
                    )}
                </div>

                {/* Is Paid Checkbox */}
                <div className="space-y-1">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            name="isPaid"
                            checked={formData.isPaid}
                            onChange={handleChange}
                            className="w-4 h-4 rounded border-surface-border bg-surface-overlay cursor-pointer transition-colors"
                            disabled={isSubmitting}
                        />
                        <span className="text-sm text-text-primary">Mark as Paid</span>
                    </label>
                </div>

                {/* Notes */}
                <div className="space-y-1">
                    <label htmlFor="notes" className="label-base flex items-center gap-1.5">
                        <StickyNote className="w-4 h-4" />
                        Notes
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="input-base resize-y"
                        placeholder="Additional notes (optional)"
                        rows={3}
                        disabled={isSubmitting}
                    />
                </div>

                {/* Invoice File */}
                <FileUploadField
                    label="Invoice File"
                    serviceUid={invoice?.serviceUid}
                    value={formData.fileUid}
                    onChange={(uid) => setFormData((prev) => ({ ...prev, fileUid: uid }))}
                    onUploadingChange={(uploading) => setIsUploading(uploading)}
                    disabled={isSubmitting}
                />

                {/* Payment Proof */}
                <FileUploadField
                    label="Payment Proof"
                    serviceUid={invoice?.serviceUid}
                    value={formData.paymentProofFileUid}
                    onChange={(uid) => setFormData((prev) => ({ ...prev, paymentProofFileUid: uid }))}
                    onUploadingChange={(uploading) => setIsUploading(uploading)}
                    disabled={isSubmitting}
                />
            </form>
        </Modal>
    );
};

export default EditInvoiceModal;
